import { lazy } from 'react';
import { googleOauthClientId as getGoogleOauthClientId } from '@gonfalon/constants/constants';
import { enableDomainMatching, isVercelMarketplaceOauthSignUpEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert } from '@launchpad-ui/components';
import cx from 'clsx';

import { GitHubOAuthButton } from '#components/loginAndSignUp/OAuthButtons/GitHubOAuthButton/GitHubOAuthButton';
import {
  GoogleDataText,
  GoogleOAuthButton,
} from '#components/loginAndSignUp/OAuthButtons/GoogleOAuthButton/GoogleOAuthButton';
import { DividerWithText } from '#components/ui/DividerWithText/DividerWithText';
import { PageWithPromo } from '#components/ui/layout/PageWithPromo';
import { UnauthenticatedPage } from '#components/ui/layout/UnauthenticatedPage';
import { Module, ModuleContent, ModuleHeader } from '#components/ui/module';
import styles from '#stylesheets/components/Login.module.css';
import { isEmailNotVerifiedError, trackSignUp } from '#utils/loginUtils';
import { shouldShowUnauthenticatedPagePromo } from '#utils/unauthenticatedPage';

import { useCaptureSignupLandingUrl } from '../signUp/useCaptureSignupLandingUrl';
import { ErrorAlert } from '../ui/alert/ErrorAlert';

import { AgentControlBanner } from './AgentControlBanner/AgentControlBanner';
import { LoginFormContainer } from './LoginFormContainer';

const VerifyMFA = lazy(async () =>
  import(/* webpackChunkName: "VerifyMFA" */ '#components/VerifyMFA').then((mod) => ({ default: mod.VerifyMFA })),
);

export type LoginProps = {
  token?: string;
  authTimedOut?: boolean;
  needsRefresh?: boolean;
  isRecovery: boolean;
  error?: ImmutableServerError;
  githubOauthError?: boolean;
  googleOauthError?: boolean;
  vercelOauthError?: boolean;
  requirePassword: boolean;
  onMFAVerify: (token: string, code: string, redirectPath?: string) => Promise<void>;
  onMFARecover: (token: string, code: string) => Promise<void>;
  onUseMFARecovery: () => void;
  rulesOfBehaviorIntro?: string;
  disallowSignups: boolean;
  beastMode?: boolean;
  beastModeEmail?: string;
  federalCssStylesEnabled: boolean;
  enableGithubOAuthSignUp: boolean;
  enableGoogleOAuthSignUp: boolean;
  redirect?: string;
};

export function Login({
  federalCssStylesEnabled,
  token,
  error,
  isRecovery,
  authTimedOut,
  needsRefresh,
  requirePassword,
  disallowSignups,
  beastMode,
  beastModeEmail,
  rulesOfBehaviorIntro,
  onMFAVerify,
  onMFARecover,
  githubOauthError,
  googleOauthError,
  vercelOauthError,
  onUseMFARecovery,
  enableGithubOAuthSignUp,
  enableGoogleOAuthSignUp,
  redirect,
}: LoginProps) {
  useCaptureSignupLandingUrl();

  const isUnverifiedEmail = isEmailNotVerifiedError(error);
  const isDomainMatchingEnabled = enableDomainMatching();
  const googleOauthClientId = getGoogleOauthClientId();
  const enableVercelOAuthSignUp = isVercelMarketplaceOauthSignUpEnabled();
  const enableOAuthSignUp = enableGoogleOAuthSignUp || enableGithubOAuthSignUp || enableVercelOAuthSignUp;

  const sanitizedUrlSearchParams = () => {
    if (window.location.search && window.location.search.length > 1) {
      const params = new URLSearchParams(window.location.search);
      params.delete('googleOauthError');
      params.delete('githubOauthError');
      params.delete('vercelOauthError');
      return `?${params.toString()}`;
    } else {
      return '';
    }
  };

  const renderLoginForm = () => (
    // @ts-expect-error withForm doesn't know about the injected props
    <LoginFormContainer
      requirePassword={requirePassword}
      beastMode={beastMode}
      beastModeEmail={beastModeEmail}
      redirect={redirect}
      isUnverifiedEmail={isUnverifiedEmail}
    />
  );

  const renderJoinExistingAcct = () => {
    if (isDomainMatchingEnabled) {
      return null;
    }

    return (
      <p>
        <Message
          id="login.signIn.joinExistingPrompt"
          values={{
            link: (chunks) => (
              <a
                onClick={() =>
                  trackSignUp('Request to Invite Link Clicked', {
                    url: window.location.pathname,
                    component: 'Login',
                    type: 'link',
                  })
                }
                href={`/join${sanitizedUrlSearchParams()}`}
              >
                {chunks}
              </a>
            ),
          }}
        />
      </p>
    );
  };

  const renderOtherActions = () => {
    if (isUnverifiedEmail) {
      return null;
    }

    return !beastMode ? (
      !disallowSignups && (
        <>
          {' '}
          <p>
            <Message
              id="login.signIn.noAccountPrompt"
              values={{
                link: (chunks) => (
                  <a
                    onClick={() =>
                      trackSignUp('Sign Up Link Clicked', {
                        url: window.location.pathname,
                        component: 'Login',
                        type: 'link',
                      })
                    }
                    href={`/signup${sanitizedUrlSearchParams()}`}
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </p>
          {renderJoinExistingAcct()}
          <p>
            <Message
              id="login.signIn.federalAccountPrompt"
              values={{ link: (chunks) => <a href="https://app.launchdarkly.us/login">{chunks}</a> }}
            />
          </p>
        </>
      )
    ) : (
      <p>
        <Message id="login.signIn.backToSettings" values={{ link: (chunks) => <a href="/settings">{chunks}</a> }} />
      </p>
    );
  };

  const renderMFA = () => (
    <VerifyMFA
      className="Login-mfa"
      token={token}
      error={error}
      isRecovery={isRecovery}
      onVerify={onMFAVerify}
      onRecover={onMFARecover}
      onUseRecovery={onUseMFARecovery}
      rulesOfBehaviorIntro={rulesOfBehaviorIntro}
      redirectPath={redirect}
      redirectQueryString={sanitizedUrlSearchParams()}
    />
  );

  const hideHeaderBackground = !token && !shouldShowUnauthenticatedPagePromo();

  return (
    <UnauthenticatedPage
      federalCssStylesEnabled={federalCssStylesEnabled}
      pageClass="Login"
      hideHeaderBackground={hideHeaderBackground}
    >
      {token ? (
        <PageWithPromo promoType="LogoGrid">{renderMFA()}</PageWithPromo>
      ) : (
        <PageWithPromo promoType="FlagControlled">
          <Module
            className={cx(styles.Login, styles.signupOptimization, {
              [styles.federal]: federalCssStylesEnabled,
            })}
          >
            <div className={styles.content} data-theme="default">
              <ModuleHeader>
                {!isUnverifiedEmail && (
                  <h1>
                    <Message id="login.signIn.heading" />
                  </h1>
                )}
                {beastMode && (
                  <h2>
                    <Message id="login.signIn.reauthenticate" />
                  </h2>
                )}
              </ModuleHeader>
              {!requirePassword && rulesOfBehaviorIntro && (
                <Alert status="error" className={styles.federalWarning}>
                  {rulesOfBehaviorIntro}
                </Alert>
              )}
              <ModuleContent isSnug className={cx({ [styles.LoginModuleFederal]: federalCssStylesEnabled })} isSeamless>
                {(authTimedOut || needsRefresh) && (
                  <Alert className="u-mb-l" status="info">
                    <Message id="login.signIn.authTimedOut" />
                  </Alert>
                )}
                {error && !isUnverifiedEmail && <ErrorAlert className="u-mb-l" error={error} />}
                {enableOAuthSignUp && (googleOauthError || githubOauthError || vercelOauthError) && !error && (
                  <ErrorAlert
                    className="u-mb-l"
                    error={{
                      message: msg('login.signIn.oauthError', {
                        provider: googleOauthError ? 'google' : githubOauthError ? 'github' : 'vercel',
                      }),
                    }}
                  />
                )}

                <>
                  <AgentControlBanner />
                  {renderLoginForm()}
                  {enableGoogleOAuthSignUp && googleOauthClientId && !isUnverifiedEmail && (
                    <>
                      <DividerWithText text={msg('login.oauthButtons.orDivider')} />
                      <div className={styles.googleButton}>
                        <GoogleOAuthButton
                          theme="outline"
                          clientId={googleOauthClientId}
                          redirect={redirect}
                          dataText={requirePassword ? GoogleDataText.SIGNIN_WITH : GoogleDataText.CONTINUE_WITH}
                        />
                      </div>
                    </>
                  )}
                  {enableGithubOAuthSignUp && !isUnverifiedEmail && (
                    <div className={styles.githubButton}>
                      <GitHubOAuthButton />
                    </div>
                  )}
                </>

                <div className={styles.otherActions}>{renderOtherActions()}</div>
                {federalCssStylesEnabled && (
                  <p>
                    <Message
                      id="login.signIn.commercialAccountPrompt"
                      values={{ link: (chunks) => <a href="https://app.launchdarkly.com/login">{chunks}</a> }}
                    />
                  </p>
                )}
              </ModuleContent>
            </div>
          </Module>
        </PageWithPromo>
      )}
    </UnauthenticatedPage>
  );
}
